import type { SizeMm } from '@repo/api-contracts';
import type { PointMm } from '@repo/design-renderer';

/**
 * 컴포넌트 mm → 화면(CSS px) 사상. 가로·세로 축척이 따로 있다 — 사진 위 인쇄 영역 자리(rect)의 비율이 실측 비율과
 * 조금 다르면 최종 렌더러(drawFlatPlacement)처럼 축마다 늘여 그 자리를 꽉 채운다. 사진이 없으면 둘이 같다.
 */
export interface FlatView {
  readonly pxPerMmX: number;
  readonly pxPerMmY: number;
  readonly offsetX: number;
  readonly offsetY: number;
}

/** 래스터를 그릴 한 해상도 — 렌더러처럼 두 축척 중 큰 쪽으로 그려 CSS 로 늘인다(작은 쪽 축은 줄어든다). */
export const rasterPxPerMmOf = (view: FlatView): number => Math.max(view.pxPerMmX, view.pxPerMmY);

/** 컴포넌트 mm 상자의 화면 크기(CSS px) — 축마다 따로 늘인다. */
export const surfaceBoxOf = (
  view: FlatView,
  component: SizeMm,
): { left: number; top: number; width: number; height: number } => ({
  left: view.offsetX,
  top: view.offsetY,
  width: component.widthMm * view.pxPerMmX,
  height: component.heightMm * view.pxPerMmY,
});

interface ViewSize {
  readonly width: number;
  readonly height: number;
}

interface ClientPoint {
  readonly x: number;
  readonly y: number;
}

interface RectOrigin {
  readonly left: number;
  readonly top: number;
}

/** 컴포넌트 mm 사각형을 여백 안에 가운데 맞춘 축척(CSS px/mm)과 오프셋. */
export const fitFlatView = (view: ViewSize, component: SizeMm, paddingPx = 24): FlatView => {
  const pxPerMm = Math.max(
    0.01,
    Math.min(
      (view.width - 2 * paddingPx) / component.widthMm,
      (view.height - 2 * paddingPx) / component.heightMm,
    ),
  );
  return {
    pxPerMmX: pxPerMm,
    pxPerMmY: pxPerMm,
    offsetX: (view.width - component.widthMm * pxPerMm) / 2,
    offsetY: (view.height - component.heightMm * pxPerMm) / 2,
  };
};

export interface PhotoBackdropRect {
  /** 사진 원본 px. */
  readonly photo: { readonly widthPx: number; readonly heightPx: number };
  /** 인쇄 영역(컴포넌트)이 사진 위에 놓이는 축 정렬 사각형 — 사진 원본 px. */
  readonly rect: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  };
}

/**
 * 목업 사진 위 평면 편집의 보기 모드.
 * - photo: 사진 전체가 화면에 들어가도록 둔다(기본, 전체 보기).
 * - area: 인쇄 영역들이 화면을 채우는 배율에서 시작해, 캔버스에서 휠·끌기로 자유롭게 확대·이동한다(영역 확대).
 */
export type FlatFitMode = 'area' | 'photo';

export interface PhotoFlatView {
  readonly view: FlatView;
  /** 화면에 놓는 사진 상자(CSS px, 편집 표면 기준). */
  readonly photo: {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
  };
}

/** 인쇄 영역이 화면을 채우는 최대 비율 — 둘레에 상품이 조금 보이게 남긴다. */
const RECT_FILL = 0.85;
/** 사진 전체가 들어가는 축척의 최대 배율 — 인쇄 영역이 작아도 상품 맥락이 사라질 만큼 당기지 않는다. */
const MAX_PHOTO_ZOOM = 1.75;

const placeAxis = (stage: number, photoSpan: number, rectCenter: number): number => {
  if (photoSpan <= stage) return (stage - photoSpan) / 2;
  const centered = stage / 2 - rectCenter;
  return Math.min(0, Math.max(stage - photoSpan, centered));
};

/** 공유 공간(사진 px 또는 가상 mm 공간)을 화면에 놓은 결과 — 배율과 왼쪽 위 위치(CSS px). */
export interface SpaceFit {
  readonly k: number;
  readonly left: number;
  readonly top: number;
}

interface SpaceRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * 공유 공간을 화면에 맞춘다. area 모드는 focus(인쇄 영역들의 외접 사각형)가 화면을 채우도록 공간을 당기고(최대 MAX_PHOTO_ZOOM),
 * photo 모드는 공간 전체가 들어가는 배율로 둔다. 공간이 화면보다 크면 focus 가운데를 화면 가운데로 당기되 빈틈은 남기지 않는다.
 */
export const fitSpaceView = (
  view: ViewSize,
  space: { readonly widthPx: number; readonly heightPx: number },
  focus: SpaceRect,
  paddingPx = 24,
  mode: FlatFitMode = 'area',
): SpaceFit => {
  const availW = Math.max(1, view.width - 2 * paddingPx);
  const availH = Math.max(1, view.height - 2 * paddingPx);
  const fitPhoto = Math.min(availW / space.widthPx, availH / space.heightPx);
  const fitRect = Math.min(availW / focus.width, availH / focus.height) * RECT_FILL;
  const k = Math.max(
    0.0001,
    mode === 'photo' ? fitPhoto : Math.min(fitRect, fitPhoto * MAX_PHOTO_ZOOM),
  );
  return {
    k,
    left: placeAxis(view.width, space.widthPx * k, (focus.x + focus.width / 2) * k),
    top: placeAxis(view.height, space.heightPx * k, (focus.y + focus.height / 2) * k),
  };
};

/**
 * 공간 위 인쇄 영역 자리(rect)에 컴포넌트 mm 상자를 겹친 보기 — 자리를 꽉 채우도록 가로·세로 축척을 따로 둔다
 * (최종 렌더러·어드민과 같은 축별 늘이기라 편집 화면이 결과물과 같은 자리·모양으로 보인다).
 */
export const surfaceViewIn = (fit: SpaceFit, rect: SpaceRect, component: SizeMm): FlatView => ({
  pxPerMmX: Math.max(0.01, (rect.width * fit.k) / component.widthMm),
  pxPerMmY: Math.max(0.01, (rect.height * fit.k) / component.heightMm),
  offsetX: fit.left + rect.x * fit.k,
  offsetY: fit.top + rect.y * fit.k,
});

/**
 * 목업 사진 위 평면 편집(인쇄 영역 하나) — fitSpaceView 에 사진을 공간으로, 인쇄 영역을 focus 로 넣은 것과 같다.
 */
export const fitFlatViewOnPhoto = (
  view: ViewSize,
  component: SizeMm,
  backdrop: PhotoBackdropRect,
  paddingPx = 24,
  mode: FlatFitMode = 'area',
): PhotoFlatView => {
  const fit = fitSpaceView(view, backdrop.photo, backdrop.rect, paddingPx, mode);
  return {
    view: surfaceViewIn(fit, backdrop.rect, component),
    photo: {
      left: fit.left,
      top: fit.top,
      width: backdrop.photo.widthPx * fit.k,
      height: backdrop.photo.heightPx * fit.k,
    },
  };
};

export const clientToComponentMm = (
  view: FlatView,
  rect: RectOrigin,
  client: ClientPoint,
): PointMm => ({
  xMm: (client.x - rect.left - view.offsetX) / view.pxPerMmX,
  yMm: (client.y - rect.top - view.offsetY) / view.pxPerMmY,
});

export const componentMmToClient = (
  view: FlatView,
  rect: RectOrigin,
  point: PointMm,
): ClientPoint => ({
  x: rect.left + view.offsetX + point.xMm * view.pxPerMmX,
  y: rect.top + view.offsetY + point.yMm * view.pxPerMmY,
});
